import React, { useEffect, useState } from 'react';
import { ProjectCardFormValues } from '../ProjectCardForm.model';
import { UseFormReturnType } from '@mantine/form';
import { CustomSelect } from '../../CustomInputFields/CustomSelect/CustomSelect';
import { FollowUpsDto } from '../../../build/generated-sources/openapi';

interface FollowUpsFormFormProps {
  form: UseFormReturnType<ProjectCardFormValues>;
  followUps?: FollowUpsDto;
  disabled?: boolean;
}

export const FollowUpsForm = ({ form, followUps, disabled }: FollowUpsFormFormProps) => {
  const [followUpPrkkPriorityAreas, setFollowUpPrkkPriorityAreas] = useState<
    { value: string; label: string }[]
  >([]);
  const [followUpPrkkSubregions, setFollowUpPrkkSubregions] = useState<
    { value: string; label: string }[]
  >([]);
  const [followUpPrkkSpecificTargets, setFollowUpPrkkSpecificTargets] = useState<
    { value: string; label: string }[]
  >([]);
  const [followUpPrkkMeasures, setFollowUpPrkkMeasures] = useState<
    { value: string; label: string }[]
  >([]);
  const [followUpSrrcrStrategicTargets, setFollowUpSrrcrStrategicTargets] = useState<
    { value: string; label: string }[]
  >([]);
  const [followUpSrrcrSpecificTargets, setFollowUpSrrcrSpecificTargets] = useState<
    { value: string; label: string }[]
  >([]);
  const [followUpSrrcrTypeMeasures, setFollowUpSrrcrTypeMeasures] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (followUps && followUps.followUpPrkkPriorityArea) {
      setFollowUpPrkkPriorityAreas(
        followUps?.followUpPrkkPriorityArea.map((entry) => {
          return { value: entry.id + '', label: entry.title ?? '' };
        })
      );
    }
    if (followUps && followUps.followUpSrrcrStrategicTarget) {
      setFollowUpSrrcrStrategicTargets(
        followUps?.followUpSrrcrStrategicTarget.map((entry) => {
          return { value: entry.id + '', label: entry.title ?? '' };
        })
      );
    }
    if (followUps && followUps.followUpSrrcrTypeMeasure) {
      setFollowUpSrrcrTypeMeasures(
        followUps?.followUpSrrcrTypeMeasure.map((entry) => {
          return { value: entry.id + '', label: entry.title ?? '' };
        })
      );
    }
  }, [followUps]);

  // PRKK: populate child data arrays based on parent selection
  useEffect(() => {
    if (form.values.followUpPrkkPriorityAreaId && followUps?.followUpPrkkSubregion) {
      const filteredData = followUps?.followUpPrkkSubregion
        .filter((entry) => entry.parentId == form.values.followUpPrkkPriorityAreaId)
        .map((entry) => ({ value: entry.id + '', label: entry.title ?? '' }));

      setFollowUpPrkkSubregions(filteredData);
    }
  }, [form.values.followUpPrkkPriorityAreaId, followUps?.followUpPrkkSubregion]);

  useEffect(() => {
    if (form.values.followUpPrkkSubregionId && followUps?.followUpPrkkSpecificTarget) {
      const filteredData = followUps?.followUpPrkkSpecificTarget
        .filter((entry) => entry.parentId == form.values.followUpPrkkSubregionId)
        .map((entry) => ({ value: entry.id + '', label: entry.title ?? '' }));

      setFollowUpPrkkSpecificTargets(filteredData);
    }
  }, [form.values.followUpPrkkSubregionId, followUps?.followUpPrkkSpecificTarget]);

  useEffect(() => {
    if (form.values.followUpPrkkSpecificTargetId && followUps?.followUpPrkkMeasure) {
      const filteredData = followUps?.followUpPrkkMeasure
        .filter((entry) => entry.parentId == form.values.followUpPrkkSpecificTargetId)
        .map((entry) => ({ value: entry.id + '', label: entry.title ?? '' }));

      setFollowUpPrkkMeasures(filteredData);
    }
  }, [form.values.followUpPrkkSpecificTargetId, followUps?.followUpPrkkMeasure]);

  // SRRČR: populate child data arrays based on parent selection
  useEffect(() => {
    if (form.values.followUpSrrcrStrategicTargetId && followUps?.followUpSrrcrSpecificTarget) {
      const filteredData = followUps?.followUpSrrcrSpecificTarget
        .filter((entry) => entry.parentId == form.values.followUpSrrcrStrategicTargetId)
        .map((entry) => ({ value: entry.id + '', label: entry.title ?? '' }));

      setFollowUpSrrcrSpecificTargets(filteredData);
    }
  }, [form.values.followUpSrrcrStrategicTargetId, followUps?.followUpSrrcrSpecificTarget]);

  // Cascade clear handlers — clear children when parent is explicitly cleared
  // Must use `null as any` because Mantine Select needs `null` (not `undefined`)
  // to show empty state in controlled mode. With `undefined` it switches to
  // uncontrolled mode and keeps displaying the old value.
  const clearPrkkFromSubregion = () => {
    form.setFieldValue('followUpPrkkSubregionId', null as any);
    form.setFieldValue('followUpPrkkSpecificTargetId', null as any);
    form.setFieldValue('followUpPrkkMeasureId', null as any);
    setFollowUpPrkkSubregions([]);
    setFollowUpPrkkSpecificTargets([]);
    setFollowUpPrkkMeasures([]);
  };

  const clearPrkkFromSpecificTarget = () => {
    form.setFieldValue('followUpPrkkSpecificTargetId', null as any);
    form.setFieldValue('followUpPrkkMeasureId', null as any);
    setFollowUpPrkkSpecificTargets([]);
    setFollowUpPrkkMeasures([]);
  };

  const clearPrkkFromMeasure = () => {
    form.setFieldValue('followUpPrkkMeasureId', null as any);
    setFollowUpPrkkMeasures([]);
  };

  const clearSrrcrFromSpecificTarget = () => {
    form.setFieldValue('followUpSrrcrSpecificTargetId', null as any);
    setFollowUpSrrcrSpecificTargets([]);
  };

  return (
    <>
      {followUps?.followUpPrkkPriorityArea && (
        <CustomSelect
          label="Návaznost na Program rozvoj Karlovarského kraje"
          data={followUpPrkkPriorityAreas}
          allowDeselect
          clearable
          disabled={disabled}
          inputProps={form.getInputProps('followUpPrkkPriorityAreaId')}
          onChangeCallback={(value) => {
            if (!value) clearPrkkFromSubregion();
          }}
        />
      )}
      {followUps?.followUpPrkkSubregion && (
        <CustomSelect
          label=""
          disabled={!form.values.followUpPrkkPriorityAreaId || disabled}
          data={followUpPrkkSubregions}
          allowDeselect
          clearable
          inputProps={form.getInputProps('followUpPrkkSubregionId')}
          onChangeCallback={(value) => {
            if (!value) clearPrkkFromSpecificTarget();
          }}
        />
      )}
      {followUps?.followUpPrkkSpecificTarget && (
        <CustomSelect
          label=""
          disabled={!form.values.followUpPrkkSubregionId || disabled}
          data={followUpPrkkSpecificTargets}
          allowDeselect
          clearable
          inputProps={form.getInputProps('followUpPrkkSpecificTargetId')}
          onChangeCallback={(value) => {
            if (!value) clearPrkkFromMeasure();
          }}
        />
      )}
      {followUps?.followUpPrkkMeasure && (
        <CustomSelect
          label=""
          disabled={!form.values.followUpPrkkSpecificTargetId || disabled}
          data={followUpPrkkMeasures}
          allowDeselect
          clearable
          inputProps={form.getInputProps('followUpPrkkMeasureId')}
        />
      )}
      {followUps?.followUpSrrcrStrategicTarget && (
        <CustomSelect
          disabled={disabled}
          label="Návaznost na Strategii regionálního rozvoje ČR"
          data={followUpSrrcrStrategicTargets}
          allowDeselect
          clearable
          inputProps={form.getInputProps('followUpSrrcrStrategicTargetId')}
          onChangeCallback={(value) => {
            if (!value) clearSrrcrFromSpecificTarget();
          }}
        />
      )}
      {followUps?.followUpSrrcrSpecificTarget && (
        <CustomSelect
          label=""
          disabled={!form.values.followUpSrrcrStrategicTargetId || disabled}
          data={followUpSrrcrSpecificTargets}
          allowDeselect
          clearable
          inputProps={form.getInputProps('followUpSrrcrSpecificTargetId')}
        />
      )}
      {followUps?.followUpSrrcrTypeMeasure && (
        <CustomSelect
          label=""
          disabled={disabled}
          data={followUpSrrcrTypeMeasures}
          allowDeselect
          clearable
          inputProps={form.getInputProps('followUpSrrcrTypeMeasureId')}
        />
      )}
    </>
  );
};
